import { API_BASE_URL } from '../utils/apiBaseUrl';

const REMOTE_ENDPOINT = process.env.REACT_APP_LOGGING_ENDPOINT || '/api/logs/frontend';
const REMOTE_URL = REMOTE_ENDPOINT.startsWith('http')
    ? REMOTE_ENDPOINT
    : `${API_BASE_URL}${REMOTE_ENDPOINT.startsWith('/') ? REMOTE_ENDPOINT : `/${REMOTE_ENDPOINT}`}`;

const LEVEL_PRIORITY = { trace: 0, debug: 1, info: 2, warn: 3, error: 4 };

let consoleThreshold = parseLevel(process.env.REACT_APP_LOG_LEVEL || 'debug');
// Avoid spamming the backend with high-volume INFO logs (e.g. routine API calls), which causes 429.
const DEFAULT_REMOTE_LOG_LEVEL = 'warn';
let remoteThreshold = parseLevel(process.env.REACT_APP_REMOTE_LOG_LEVEL || DEFAULT_REMOTE_LOG_LEVEL);
let remoteEnabled = process.env.REACT_APP_ENABLE_REMOTE_LOGGING !== 'false';

const listeners = new Set();
let initialized = false;

// Remote logging: queue + rate limiting + backoff on 429 to prevent log storms.
const REMOTE_QUEUE_MAX = parseInt(process.env.REACT_APP_REMOTE_LOG_QUEUE_MAX || '200', 10);
const REMOTE_MIN_INTERVAL_MS = parseInt(process.env.REACT_APP_REMOTE_LOG_MIN_INTERVAL_MS || '400', 10);
const REMOTE_IDLE_FLUSH_DELAY_MS = parseInt(process.env.REACT_APP_REMOTE_LOG_FLUSH_DELAY_MS || '250', 10);
const REMOTE_BACKOFF_BASE_MS = parseInt(process.env.REACT_APP_REMOTE_LOG_BACKOFF_BASE_MS || '5000', 10);
const REMOTE_BACKOFF_MAX_MS = parseInt(process.env.REACT_APP_REMOTE_LOG_BACKOFF_MAX_MS || String(5 * 60 * 1000), 10);

let remoteQueue = [];
let remoteFlushTimer = null;
let remoteFlushing = false;
let remoteLastSendAt = 0;
let remoteCooldownUntil = 0;
let remoteBackoffMs = 0;

export function initializeLogging() {
    if (initialized) {
        return;
    }
    initialized = true;

    if (typeof window !== 'undefined') {
        window.addEventListener('error', (event) => {
            const error = event.error instanceof Error
                ? event.error
                : new Error(String(event.message ?? 'Unbekannter Fehler'));
            logError('Uncaught error', error, {
                component: 'window',
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
            });
        });

        window.addEventListener('unhandledrejection', (event) => {
            const reason = event.reason instanceof Error
                ? event.reason
                : new Error(String(event.reason ?? 'Promise rejected without reason'));
            logError('Unhandled promise rejection', reason, {
                component: 'window',
            });
        });
    }

    logInfo('Frontend logging initialisiert', {
        component: 'loggingService',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    });
}

export function setConsoleLogLevel(level) {
    consoleThreshold = parseLevel(level);
}

export function setRemoteLogLevel(level) {
    remoteThreshold = parseLevel(level);
}

export function enableRemoteLogging(enabled) {
    remoteEnabled = Boolean(enabled);
}

export function subscribeToLogs(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function logTrace(message, context) {
    return log('trace', message, context);
}

export function logDebug(message, context) {
    return log('debug', message, context);
}

export function logInfo(message, context) {
    return log('info', message, context);
}

export function logWarn(message, context) {
    return log('warn', message, context);
}

export function logError(message, errorOrContext, optionalContext) {
    let error;
    let context = {};

    if (errorOrContext instanceof Error) {
        error = errorOrContext;
        if (optionalContext && typeof optionalContext === 'object') {
            context = { ...optionalContext };
        }
    } else if (errorOrContext && typeof errorOrContext === 'object') {
        context = { ...errorOrContext };
    } else if (errorOrContext !== undefined) {
        context = { detail: errorOrContext };
    }

    if (error) {
        context.error = error;
    }

    return log('error', message, context);
}

function log(level, message, context = {}) {
    const normalizedLevel = (level || 'info').toLowerCase();
    const severity = LEVEL_PRIORITY[normalizedLevel] ?? LEVEL_PRIORITY.info;
    const timestamp = new Date().toISOString();

    let component;
    if (context && typeof context === 'object' && !Array.isArray(context)) {
        if (typeof context.component === 'string') {
            component = context.component;
        }
    }

    const sanitizedContext = sanitizeContext(context);
    let stackTrace;
    if (sanitizedContext && sanitizedContext.error && sanitizedContext.error.stack) {
        stackTrace = sanitizedContext.error.stack;
        sanitizedContext.error = { ...sanitizedContext.error };
        delete sanitizedContext.error.stack;
    }

    const entry = {
        level: normalizedLevel,
        message: message ?? '',
        timestamp,
        component,
        context: sanitizedContext,
    };

    if (stackTrace) {
        entry.stackTrace = stackTrace;
    }

    if (severity >= consoleThreshold) {
        const consoleMethod = getConsoleMethod(normalizedLevel);
        const consolePayload = entry.context ? { ...entry.context } : {};
        if (stackTrace) {
            consolePayload.stackTrace = stackTrace;
        }
        console[consoleMethod](`[${timestamp}] [${normalizedLevel.toUpperCase()}] ${message}`, consolePayload);
    }

    notifyListeners(entry);

    if (remoteEnabled && severity >= remoteThreshold) {
        enqueueRemote(entry);
    }

    return entry;
}

function enqueueRemote(entry) {
    if (!remoteEnabled) {
        return;
    }

    // Drop oldest if queue is full to avoid unbounded memory growth during outages/backoff.
    if (remoteQueue.length >= REMOTE_QUEUE_MAX) {
        remoteQueue.shift();
    }
    remoteQueue.push(entry);

    scheduleRemoteFlush(REMOTE_IDLE_FLUSH_DELAY_MS);
}

function scheduleRemoteFlush(delayMs) {
    if (remoteFlushTimer) {
        return;
    }

    remoteFlushTimer = setTimeout(() => {
        remoteFlushTimer = null;
        flushRemoteQueue().catch(() => {
            // Errors are intentionally swallowed to prevent logging loops.
        });
    }, Math.max(0, delayMs));
}

function parseRetryAfterMs(retryAfterHeader) {
    if (!retryAfterHeader) {
        return null;
    }

    // Retry-After can be either seconds or an HTTP-date.
    const seconds = parseInt(retryAfterHeader, 10);
    if (!Number.isNaN(seconds)) {
        return seconds * 1000;
    }

    const asDate = Date.parse(retryAfterHeader);
    if (!Number.isNaN(asDate)) {
        return Math.max(0, asDate - Date.now());
    }

    return null;
}

function registerRemoteBackoffMs(ms) {
    const normalized = Math.min(Math.max(0, ms), REMOTE_BACKOFF_MAX_MS);
    remoteCooldownUntil = Date.now() + normalized;
}

async function flushRemoteQueue() {
    if (!remoteEnabled) {
        remoteQueue = [];
        return;
    }

    if (remoteFlushing) {
        return;
    }
    remoteFlushing = true;
    try {
        while (remoteQueue.length > 0) {
            const now = Date.now();

            if (now < remoteCooldownUntil) {
                scheduleRemoteFlush(remoteCooldownUntil - now);
                return;
            }

            const elapsedSinceLastSend = now - remoteLastSendAt;
            if (elapsedSinceLastSend < REMOTE_MIN_INTERVAL_MS) {
                scheduleRemoteFlush(REMOTE_MIN_INTERVAL_MS - elapsedSinceLastSend);
                return;
            }

            const entry = remoteQueue.shift();
            if (!entry) {
                return;
            }

            remoteLastSendAt = Date.now();
            const response = await sendToServer(entry);

            // Handle rate limiting explicitly.
            if (response && response.status === 429) {
                const retryAfterMs = parseRetryAfterMs(response.headers?.get?.('Retry-After'));
                remoteBackoffMs = remoteBackoffMs > 0
                    ? Math.min(remoteBackoffMs * 2, REMOTE_BACKOFF_MAX_MS)
                    : REMOTE_BACKOFF_BASE_MS;
                registerRemoteBackoffMs(retryAfterMs ?? remoteBackoffMs);
                // Prune low-priority queue items on 429 rate limit to avoid queue accumulation
                remoteQueue = remoteQueue.filter((item) => (LEVEL_PRIORITY[item?.level] ?? 0) >= LEVEL_PRIORITY.warn);
                scheduleRemoteFlush(remoteCooldownUntil - Date.now());
                return;
            }

            // Treat other non-2xx as transient; apply a smaller backoff to avoid tight loops.
            if (response && !response.ok) {
                remoteBackoffMs = remoteBackoffMs > 0
                    ? Math.min(remoteBackoffMs * 2, REMOTE_BACKOFF_MAX_MS)
                    : REMOTE_BACKOFF_BASE_MS;
                registerRemoteBackoffMs(remoteBackoffMs);
                scheduleRemoteFlush(remoteCooldownUntil - Date.now());
                return;
            }

            // Success resets backoff.
            remoteBackoffMs = 0;

            // Keep draining, but never in a tight loop: enforce spacing via REMOTE_MIN_INTERVAL_MS.
            scheduleRemoteFlush(REMOTE_MIN_INTERVAL_MS);
            return;
        }
    } finally {
        remoteFlushing = false;
    }
}

function notifyListeners(entry) {
    listeners.forEach((listener) => {
        try {
            listener(entry);
        } catch (err) {
            // Listener-Fehler werden unterdrückt, damit das Logging nicht blockiert.
        }
    });
}

function getConsoleMethod(level) {
    switch (level) {
        case 'error':
            return 'error';
        case 'warn':
            return 'warn';
        case 'info':
            return 'info';
        case 'debug':
            return 'debug';
        default:
            return 'debug';
    }
}

function parseLevel(level) {
    const normalized = typeof level === 'string' ? level.toLowerCase() : '';
    return LEVEL_PRIORITY[normalized] ?? LEVEL_PRIORITY.info;
}

function sanitizeContext(context) {
    if (!context || typeof context !== 'object') {
        return undefined;
    }

    if (Array.isArray(context)) {
        const sanitizedArray = context
            .map((value, index) => sanitizeValue(`${index}`, value))
            .filter((value) => value !== undefined);
        return sanitizedArray.length ? sanitizedArray : undefined;
    }

    const sanitized = {};
    Object.entries(context).forEach(([key, value]) => {
        if (key === 'component') {
            return;
        }
        const sanitizedValue = sanitizeValue(key, value);
        if (sanitizedValue !== undefined) {
            sanitized[key] = sanitizedValue;
        }
    });

    return Object.keys(sanitized).length ? sanitized : undefined;
}

function sanitizeValue(key, value) {
    if (value === undefined) {
        return undefined;
    }
    if (value === null) {
        return null;
    }

    const lowerKey = typeof key === 'string' ? key.toLowerCase() : '';

    if (value instanceof Error) {
        return formatError(value);
    }

    if (typeof value === 'string') {
        return shouldRedact(lowerKey)
            ? '<redacted>'
            : truncate(value);
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'bigint') {
        return value.toString();
    }

    if (value instanceof Date) {
        return value.toISOString();
    }

    if (Array.isArray(value)) {
        const result = value
            .map((item, index) => sanitizeValue(`${key}[${index}]`, item))
            .filter((item) => item !== undefined);
        return result.length ? result : undefined;
    }

    if (typeof Headers !== 'undefined' && value instanceof Headers) {
        const headers = {};
        value.forEach((headerValue, headerKey) => {
            headers[headerKey] = headerKey.toLowerCase() === 'authorization'
                ? '<redacted>'
                : headerValue;
        });
        return headers;
    }

    if (typeof FormData !== 'undefined' && value instanceof FormData) {
        const formData = {};
        value.forEach((formValue, formKey) => {
            if (typeof formValue === 'string') {
                formData[formKey] = shouldRedact(formKey.toLowerCase()) ? '<redacted>' : truncate(formValue);
            } else {
                formData[formKey] = '[Binary]';
            }
        });
        return formData;
    }

    if (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) {
        const params = {};
        value.forEach((paramValue, paramKey) => {
            params[paramKey] = shouldRedact(paramKey.toLowerCase()) ? '<redacted>' : truncate(paramValue);
        });
        return params;
    }

    if (typeof PerformanceEntry !== 'undefined' && value instanceof PerformanceEntry) {
        return {
            name: value.name,
            entryType: value.entryType,
            startTime: value.startTime,
            duration: value.duration,
        };
    }

    if (typeof value === 'object') {
        const entries = Object.entries(value).reduce((acc, [childKey, childValue]) => {
            const sanitized = sanitizeValue(childKey, childValue);
            if (sanitized !== undefined) {
                acc[childKey] = sanitized;
            }
            return acc;
        }, {});
        return Object.keys(entries).length ? entries : undefined;
    }

    if (typeof value === 'symbol') {
        return value.toString();
    }

    return truncate(String(value));
}

function shouldRedact(key) {
    return key.includes('password') || key.includes('token') || key.includes('secret');
}

function truncate(value, maxLength = 1000) {
    if (typeof value !== 'string') {
        return value;
    }
    if (value.length <= maxLength) {
        return value;
    }
    return `${value.slice(0, maxLength)}…(truncated)`;
}

function formatError(error) {
    return {
        name: error.name,
        message: error.message,
        stack: truncate(error.stack ?? '', 4000),
    };
}

function getStoredToken() {
    try {
        return localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || null;
    } catch (error) {
        return null;
    }
}

async function sendToServer(entry) {
    const payload = JSON.stringify(entry);
    const token = getStoredToken();

    const headers = { 'Content-Type': 'application/json' };
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    try {
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function' && entry.level === 'error') {
            const blob = new Blob([payload], { type: 'application/json' });
            if (navigator.sendBeacon(REMOTE_URL, blob)) {
                return { ok: true, status: 204, headers: new Headers() };
            }
        }
    } catch (error) {
        // sendBeacon fehlgeschlagen – wir versuchen es per fetch.
    }

    try {
        return await fetch(REMOTE_URL, {
            method: 'POST',
            headers,
            body: payload,
            keepalive: true,
        });
    } catch (error) {
        // Network/client blocking: back off so we don't spam the console or the network stack.
        remoteBackoffMs = remoteBackoffMs > 0
            ? Math.min(remoteBackoffMs * 2, REMOTE_BACKOFF_MAX_MS)
            : REMOTE_BACKOFF_BASE_MS;
        registerRemoteBackoffMs(remoteBackoffMs);
        return { ok: false, status: 0, headers: new Headers() };
    }
}

export function flushRemoteLogs() {
    scheduleRemoteFlush(0);
}
